import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import { useAlert } from '../../../contexts/AlertContext';
import EmployeeSearch from '../../common/EmployeeSearch';
import EmptyState from '../../common/EmptyState';
import { REVIEW_TYPES } from '../../../constants/reviewTypes';
import {
  getTemplateTypeConstant,
  getTemplateTypeText,
  getTemplateTypeColor,
  getTemplateTypeBorderColor,
  MATRIX_TEMPLATE_TYPES,
} from '../../../constants/matrixTemplateTypes';

/**
 * Shared "Assign Reviewer to <name>" dialog.
 *
 * Extracted from AssignReviewer so the feedback-session setup flow (edit-reviewers)
 * and the report page admin affordance (ADR-003 Decision 7) render the exact same
 * UI: a consultant search plus a template picker filtered to the assignee's skills
 * matrix, and an optional "Assign Team Members" shortcut for parallel reviews.
 *
 * The dialog owns only the *selection* (which consultant, which template). It never
 * persists anything itself — the parent decides what an assignment means:
 *   - onConfirm({ reviewer, template })  → single reviewer
 *   - onAssignTeam(reviewerAssignments)  → bulk team list ([{ reviewer, template }])
 * In setup the parent mutates local state; on the report it calls the API.
 *
 * Props:
 *   open               Boolean
 *   onClose            () => void
 *   employeeToReview   { id, fullName, directManager? } — the assignee being reviewed
 *   existingReviewers  Array<{ reviewer: { id } }> already on the assignee
 *   matrix             { id, name } — the assignee's skills matrix
 *   templates          Array of session skills-matrix templates (filtered here by matrix)
 *   reviewType         REVIEW_TYPES.PARALLEL | REVIEW_TYPES.SEQUENTIAL
 *   team               Optional resolved team ({ employees, ... }) enabling the bulk button
 *   onConfirm          ({ reviewer, template }) => void
 *   onAssignTeam       (reviewerAssignments) => void
 *   submitting         Boolean — disables the action buttons while a parent request is in flight
 */
const ReviewerAssignmentDialog = ({
  open,
  onClose,
  employeeToReview,
  existingReviewers = [],
  matrix,
  templates = [],
  reviewType,
  team = null,
  onConfirm,
  onAssignTeam,
  submitting = false,
}) => {
  const { showAlert } = useAlert();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Clear the selection whenever the dialog opens for (or switches) an assignee.
  useEffect(() => {
    if (!open) {
      setSelectedEmployee(null);
      setSelectedTemplate(null);
    }
  }, [open, employeeToReview]);

  // Templates configured for this assignee's skills matrix.
  const getAvailableTemplates = () => {
    if (!employeeToReview || !matrix) {
      return [];
    }
    return templates.filter((template) => template.skillsMatrix && template.skillsMatrix.id === matrix.id);
  };

  // Consultants already reviewing this assignee should not be selectable again.
  const getDisabledEmployeeIds = () => existingReviewers.map((r) => r.reviewer.id);

  const isSelfAssessment = selectedEmployee?.id === employeeToReview?.id;

  // Auto-select the natural template once a reviewer is chosen, mirroring setup.
  useEffect(() => {
    if (!selectedEmployee || !employeeToReview) {
      return;
    }
    const available = getAvailableTemplates();
    if (selectedEmployee.id === employeeToReview.id) {
      setSelectedTemplate(
        available.find((t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT)
      );
    } else if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
      setSelectedTemplate(
        available.find((t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.TECHNICAL_ASSESSMENT)
      );
    } else {
      setSelectedTemplate(
        available.find((t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.PEER_REVIEW)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, employeeToReview, reviewType]);

  const hasNoReviewersOrHasSelfAssessment =
    employeeToReview &&
    (existingReviewers.length === 0 ||
      (existingReviewers.length === 1 && existingReviewers[0].reviewer.id === employeeToReview.id));

  const showTeamButton =
    !!team && reviewType === REVIEW_TYPES.PARALLEL && hasNoReviewersOrHasSelfAssessment;

  const handleConfirm = () => {
    if (!selectedEmployee || !selectedTemplate || !employeeToReview) {
      return;
    }
    onConfirm?.({ reviewer: selectedEmployee, template: selectedTemplate });
  };

  // Build the full set of reviewer assignments for the assignee's team: a peer
  // review for each teammate (excluding the assignee and their direct manager),
  // a self-assessment, and a manager review where a direct manager is known.
  const handleAssignTeam = () => {
    if (!team) {
      return;
    }
    const available = getAvailableTemplates();
    const selfAssessmentTemplate = available.find(
      (t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT
    );
    const peerReviewTemplate = available.find(
      (t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.PEER_REVIEW
    );
    const managerReviewTemplate = available.find(
      (t) => getTemplateTypeConstant(t.type) === MATRIX_TEMPLATE_TYPES.MANAGER_REVIEW
    );

    if (!selfAssessmentTemplate || !peerReviewTemplate) {
      showAlert('Required templates not found', 'error');
      return;
    }

    const reviewerAssignments = (team.employees || [])
      .filter(
        (employee) =>
          employee.id !== employeeToReview.id &&
          !(employeeToReview.directManager && employee.id === employeeToReview.directManager.id)
      )
      .map((employee) => ({ reviewer: employee, template: peerReviewTemplate }));

    reviewerAssignments.push({ reviewer: employeeToReview, template: selfAssessmentTemplate });

    if (employeeToReview.directManager && managerReviewTemplate) {
      reviewerAssignments.push({
        reviewer: {
          id: employeeToReview.directManager.id,
          fullName: employeeToReview.directManager.fullName,
          email: employeeToReview.directManager.email,
          azureId: employeeToReview.directManager.azureId,
        },
        template: managerReviewTemplate,
      });
    }

    onAssignTeam?.(reviewerAssignments);
  };

  const availableTemplates = getAvailableTemplates();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {employeeToReview ? <>Assign Reviewer to {employeeToReview.fullName}</> : <>Select Reviewer</>}
      </DialogTitle>
      <DialogContent>
        {showTeamButton && (
          <Box sx={{ mb: 3, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleAssignTeam}
              fullWidth
              disabled={submitting}
              sx={{
                borderColor: '#E2E8F0',
                color: '#475569',
                p: 2,
                justifyContent: 'flex-start',
                '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1 }} />
                <Box>
                  <Typography sx={{ fontWeight: 500, textAlign: 'left' }}>Assign Team Members</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748B' }}>
                    Automatically assign all team members as reviewers
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 2, color: '#475569' }}>
          {showTeamButton ? 'Or select individual reviewer:' : 'Add another reviewer:'}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <EmployeeSearch
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            label="Select Individual"
            placeholder="Search reviewers..."
            width={500}
            height="56px"
            sx={{ width: '100%' }}
            disabledEmployeeIds={getDisabledEmployeeIds()}
            showSelectedAvatar={true}
          />

          {selectedEmployee && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#475569' }}>
                Select template for review:
              </Typography>
              {availableTemplates.length === 0 ? (
                <EmptyState
                  title="No Templates Available"
                  description="There are no templates configured for this skills matrix. Please configure templates before assigning reviewers."
                  textSize="body1"
                />
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 2,
                  }}
                >
                  {availableTemplates.map((template) => {
                    const templateType = getTemplateTypeConstant(template.type);
                    const isSelfAssessmentTemplate = templateType === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT;
                    const isClientFeedbackTemplate = templateType === MATRIX_TEMPLATE_TYPES.CLIENT_FEEDBACK;
                    const isDisabled =
                      isClientFeedbackTemplate ||
                      (isSelfAssessment ? !isSelfAssessmentTemplate : isSelfAssessmentTemplate);

                    return (
                      <Box
                        key={template.id}
                        onClick={() => !isDisabled && setSelectedTemplate(template)}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor:
                            selectedTemplate?.id === template.id
                              ? getTemplateTypeBorderColor(getTemplateTypeConstant(template.type))
                              : '#E2E8F0',
                          borderRadius: 1,
                          backgroundColor:
                            selectedTemplate?.id === template.id
                              ? `${getTemplateTypeColor(getTemplateTypeConstant(template.type))}15`
                              : isDisabled
                              ? '#F1F5F9'
                              : '#FFFFFF',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.6 : 1,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': !isDisabled
                            ? {
                                borderColor: getTemplateTypeBorderColor(getTemplateTypeConstant(template.type)),
                                backgroundColor: '#F8FAFC',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              }
                            : {},
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography sx={{ fontWeight: 500, color: isDisabled ? '#94A3B8' : 'inherit' }}>
                            {template.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: isDisabled
                                ? '#94A3B8'
                                : getTemplateTypeColor(getTemplateTypeConstant(template.type)),
                              fontSize: '0.85rem',
                              fontStyle: 'italic',
                            }}
                          >
                            {getTemplateTypeText(getTemplateTypeConstant(template.type))}
                          </Typography>
                        </Box>
                        {template.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDisabled ? '#94A3B8' : '#64748B',
                              fontSize: '0.85rem',
                              fontStyle: 'italic',
                            }}
                          >
                            {template.description}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!selectedEmployee || !selectedTemplate || submitting}
        >
          {submitting ? <CircularProgress size={18} /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewerAssignmentDialog;
